import { useState } from 'react';
import { ProfileEditModal } from '@/components/candidate/ProfileEditModal';
import { useCreateMembership, useUpdateMembership } from '@/lib/api/hooks';
import { IonToast } from '@ionic/react';
import type { ProfessionalAssociationMembership } from '@/types';

const ASSOCIATIONS = [
  'Engineering Council of South Africa (ECSA)',
  'South African Institute of Chartered Accountants (SAICA)',
  'South African Council for Scientific and Industrial Research (CSIR)',
  'Construction Industry Development Board (CIDB)',
  'South African Qualifications Authority (SAQA)',
  'Council for Geoscience',
  'South African National Accreditation System (SANAS)',
  'Other',
];

interface MembershipFormModalProps {
  open: boolean;
  onClose: () => void;
  membership?: ProfessionalAssociationMembership | null;
  legalEntityId: string;
}

export function MembershipFormModal({ open, onClose, membership, legalEntityId }: MembershipFormModalProps) {
  const createMembership = useCreateMembership();
  const updateMembership = useUpdateMembership();
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);

  const [association, setAssociation] = useState(membership?.association.name ?? '');
  const [membershipNumber, setMembershipNumber] = useState(membership?.membershipNumber ?? '');
  const [membershipLevel, setMembershipLevel] = useState(membership?.membershipLevel ?? '');
  const [issuedDate, setIssuedDate] = useState(membership?.issuedDate ?? '');
  const [expiryDate, setExpiryDate] = useState(membership?.expiryDate ?? '');

  const isEditing = !!membership?._id;
  const isPending = createMembership.isPending || updateMembership.isPending;

  const handleSave = () => {
    if (!association.trim() || !membershipNumber.trim()) {
      setToast({ msg: 'Association and membership number are required', color: 'danger' });
      return;
    }

    if (isEditing && membership._id) {
      updateMembership.mutate(
        {
          id: membership._id,
          data: {
            association: { name: association },
            membershipNumber,
            membershipLevel: membershipLevel || undefined,
            issuedDate: issuedDate || undefined,
            expiryDate: expiryDate || undefined,
          },
        },
        {
          onSuccess: () => { setToast({ msg: 'Membership updated', color: 'success' }); onClose(); },
          onError: (err: Error) => { setToast({ msg: err.message || 'Failed to update', color: 'danger' }); },
        },
      );
    } else {
      createMembership.mutate(
        {
          legalEntityId,
          association: { name: association },
          membershipNumber,
          membershipLevel: membershipLevel || undefined,
          issuedDate: issuedDate || undefined,
          expiryDate: expiryDate || undefined,
        },
        {
          onSuccess: () => { setToast({ msg: 'Membership added', color: 'success' }); onClose(); },
          onError: (err: Error) => { setToast({ msg: err.message || 'Failed to add', color: 'danger' }); },
        },
      );
    }
  };

  return (
    <>
      <ProfileEditModal open={open} onClose={onClose} title={isEditing ? 'Edit Membership' : 'Add Membership'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Association</label>
            <select
              value={association}
              onChange={(e) => setAssociation(e.target.value)}
              style={selectStyle}
            >
              <option value="">Select association…</option>
              {ASSOCIATIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Membership number</label>
            <input
              type="text" value={membershipNumber}
              onChange={(e) => setMembershipNumber(e.target.value)}
              placeholder="e.g. PR 12345"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Level (optional)</label>
            <input
              type="text" value={membershipLevel}
              onChange={(e) => setMembershipLevel(e.target.value)}
              placeholder="e.g. Professional Member"
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Issue date</label>
              <input
                type="date" value={issuedDate}
                onChange={(e) => setIssuedDate(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Expiry date</label>
              <input
                type="date" value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="button" onClick={onClose}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: '0.85rem',
                background: 'rgba(255,255,255,0.05)', border: 'none',
                color: 'var(--bp-text-muted, #64748b)', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button" onClick={handleSave} disabled={isPending}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: '0.85rem',
                background: 'var(--bp-blue-500, #2563eb)', border: 'none',
                color: '#fff', cursor: 'pointer',
              }}
            >
              {isPending ? 'Saving…' : isEditing ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      </ProfileEditModal>
      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2000} onDidDismiss={() => setToast(null)} />
    </>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8rem', fontWeight: 500,
  color: 'var(--bp-text-secondary, #94a3b8)', marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: 'var(--bp-text, #f1f5f9)', fontSize: '0.85rem',
  outline: 'none', boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: 'var(--bp-text, #f1f5f9)', fontSize: '0.85rem',
  outline: 'none', boxSizing: 'border-box',
};
